import { useMemo, useState } from "react";
import type { DragonDossier, Faction, LairRoom } from "../types";
import { EditableText } from "./Editable";

interface InspectorProps {
  dossier: DragonDossier;
  room: LairRoom;
  faction: Faction | null;
  onEdit: (mutator: (draft: DragonDossier) => void) => void;
  onSelectRoom: (id: string) => void;
  onClearFaction: () => void;
}

export function Inspector({
  dossier,
  room,
  faction,
  onEdit,
  onSelectRoom,
  onClearFaction
}: InspectorProps) {
  const [targetRoom, setTargetRoom] = useState("");
  const roomIndex = dossier.lair.rooms.findIndex((candidate) => candidate.id === room.id);
  const factionIndex = faction
    ? dossier.factions.findIndex((candidate) => candidate.id === faction.id)
    : -1;
  const connections = useMemo(
    () =>
      dossier.lair.connections.filter(
        (connection) => connection.from === room.id || connection.to === room.id
      ),
    [dossier.lair.connections, room.id]
  );

  if (faction && factionIndex >= 0) {
    return (
      <aside className="inspector ornate-panel" aria-label="Faction inspector">
        <div className="panel-heading">
          <button type="button" className="plain-button" onClick={onClearFaction}>
            ← Room inspector
          </button>
        </div>
        <div className="inspector__number" style={{ borderColor: faction.color }}>
          {faction.power}
        </div>
        <EditableText
          label="Faction name"
          className="inspector__title"
          value={faction.name}
          onChange={(value) =>
            onEdit((draft) => {
              draft.factions[factionIndex].name = value;
            })
          }
        />
        <div className="inspector__subtitle">{faction.kind}</div>
        <dl className="inspector-list">
          <div>
            <dt>Relation</dt>
            <dd>
              <select
                value={faction.relation}
                onChange={(event) =>
                  onEdit((draft) => {
                    draft.factions[factionIndex].relation = event.target.value as Faction["relation"];
                    const relationship = draft.relationships.find(
                      (candidate) => candidate.source === faction.id || candidate.target === faction.id
                    );
                    if (relationship) {
                      relationship.type = event.target.value as Faction["relation"];
                      relationship.label = event.target.value;
                    }
                  })
                }
              >
                <option>worships</option>
                <option>fears</option>
                <option>bargains</option>
                <option>spies</option>
                <option>rivals</option>
                <option>serves</option>
              </select>
            </dd>
          </div>
          <div>
            <dt>Power</dt>
            <dd>
              <input
                type="number"
                min="1"
                max="10"
                value={faction.power}
                onChange={(event) =>
                  onEdit((draft) => {
                    draft.factions[factionIndex].power = Number(event.target.value);
                  })
                }
              />
            </dd>
          </div>
          <div className="inspector-list__wide">
            <dt>Goal</dt>
            <dd>
              <EditableText
                label="Faction goal"
                multiline
                value={faction.goal}
                onChange={(value) =>
                  onEdit((draft) => {
                    draft.factions[factionIndex].goal = value;
                  })
                }
              />
            </dd>
          </div>
        </dl>
      </aside>
    );
  }

  return (
    <aside className="inspector ornate-panel" aria-label="Room inspector">
      <div className="panel-heading">Room inspector</div>
      <div className="inspector__identity">
        <div className="inspector__number">{roomIndex + 1}</div>
        <div>
          <EditableText
            label="Room name"
            className="inspector__title"
            value={room.name}
            onChange={(value) =>
              onEdit((draft) => {
                draft.lair.rooms[roomIndex].name = value;
              })
            }
          />
          <div className="inspector__subtitle">{room.role.replace("-", " ")}</div>
        </div>
      </div>
      <dl className="inspector-list">
        <div>
          <dt>Type</dt>
          <dd>
            <select
              value={room.role}
              onChange={(event) =>
                onEdit((draft) => {
                  draft.lair.rooms[roomIndex].role = event.target.value as LairRoom["role"];
                })
              }
            >
              <option value="approach">Approach</option>
              <option value="entrance">Entrance</option>
              <option value="chokepoint">Chokepoint</option>
              <option value="quarters">Quarters</option>
              <option value="false-hoard">False hoard</option>
              <option value="hoard">Hoard</option>
              <option value="sanctum">Sanctum</option>
              <option value="escape">Escape</option>
              <option value="anomaly">Anomaly</option>
            </select>
          </dd>
        </div>
        <div>
          <dt>Danger</dt>
          <dd>
            <select
              value={room.danger}
              onChange={(event) =>
                onEdit((draft) => {
                  draft.lair.rooms[roomIndex].danger = event.target.value as LairRoom["danger"];
                })
              }
            >
              <option value="low">Low</option>
              <option value="moderate">Moderate</option>
              <option value="high">High</option>
              <option value="mythic">Mythic</option>
            </select>
          </dd>
        </div>
        <div className="inspector-list__wide">
          <dt>Occupants</dt>
          <dd>
            <EditableText
              label="Room occupants"
              value={room.occupants}
              onChange={(value) =>
                onEdit((draft) => {
                  draft.lair.rooms[roomIndex].occupants = value;
                })
              }
            />
          </dd>
        </div>
        <div className="inspector-list__wide">
          <dt>Hazards</dt>
          <dd>
            <EditableText
              label="Room hazards"
              value={room.hazards.join(", ")}
              onChange={(value) =>
                onEdit((draft) => {
                  draft.lair.rooms[roomIndex].hazards = value
                    .split(",")
                    .map((hazard) => hazard.trim())
                    .filter(Boolean);
                })
              }
            />
          </dd>
        </div>
        <div className="inspector-list__wide">
          <dt>Treasure</dt>
          <dd>
            <EditableText
              label="Room treasure"
              value={room.treasure}
              onChange={(value) =>
                onEdit((draft) => {
                  draft.lair.rooms[roomIndex].treasure = value;
                })
              }
            />
          </dd>
        </div>
        <div className="inspector-list__wide">
          <dt>Tactical note</dt>
          <dd>
            <EditableText
              label="Room tactical note"
              multiline
              rows={4}
              value={room.tacticalNote}
              onChange={(value) =>
                onEdit((draft) => {
                  draft.lair.rooms[roomIndex].tacticalNote = value;
                })
              }
            />
          </dd>
        </div>
      </dl>
      <div className="connection-editor">
        <div className="connection-editor__title">Connections</div>
        {connections.map((connection) => {
          const otherId = connection.from === room.id ? connection.to : connection.from;
          const other = dossier.lair.rooms.find((candidate) => candidate.id === otherId);
          return (
            <div className="connection-chip" key={connection.id}>
              <button type="button" onClick={() => onSelectRoom(otherId)}>
                {other?.name ?? "Missing room"}
              </button>
              <button
                type="button"
                aria-label={`Remove connection to ${other?.name}`}
                onClick={() =>
                  onEdit((draft) => {
                    draft.lair.connections = draft.lair.connections.filter(
                      (candidate) => candidate.id !== connection.id
                    );
                  })
                }
              >
                ×
              </button>
            </div>
          );
        })}
        <div className="connection-add">
          <select value={targetRoom} onChange={(event) => setTargetRoom(event.target.value)}>
            <option value="">Connect room…</option>
            {dossier.lair.rooms
              .filter((candidate) => candidate.id !== room.id)
              .map((candidate) => (
                <option value={candidate.id} key={candidate.id}>
                  {candidate.name}
                </option>
              ))}
          </select>
          <button
            type="button"
            disabled={!targetRoom}
            onClick={() => {
              if (!targetRoom) return;
              onEdit((draft) => {
                draft.lair.connections.push({
                  id: `path-custom-${crypto.randomUUID()}`,
                  from: room.id,
                  to: targetRoom,
                  secret: false,
                  escape: false
                });
              });
              setTargetRoom("");
            }}
          >
            Add
          </button>
        </div>
      </div>
    </aside>
  );
}
